import {
	Box,
	Button,
	Flex,
	type FlexProps,
	Grid,
	Spinner,
	Text,
	Textarea,
	useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useTicket, useTicketMessages } from "../hooks/tickets.hooks";
import { useSimpleToast } from "../hooks/toast.hooks";
import { useConnectedUser, useUser } from "../hooks/user.hooks";
import { linkFilesToTicket, updateTicket } from "../mutations/ticket.mutations";
import { socket } from "../socket";
import { TicketStatus } from "../types/ticket.types";
import { userIsAdmin } from "../utils";
import AttachedFileIcon from "./icons/AttachedFileIcon";
import SendIcon from "./icons/SendIcon";
import AttachedFileModal from "./modals/AttachedFileModal";
import SimpleModal from "./modals/SimpleModal";
import SupemboutsAvatarIcon from "./icons/SupemboutsAvatarIcon";
import DefaultAvatarIcon from "./icons/DefaultAvatarIcon";
import type { User } from "../types/user.types";
import PreviewFile from "./PreviewFile";

type TicketMeta = {
	room: {
		name: string;
	};
	author: {
		id: number | null;
	};
	ticket: {
		id: number | null;
	};
};

type TicketMessage = {
	content: string;
	meta: object | null;
	createdAt: Date;
	updatedAt: Date;
	authorId: number;
};

const ChatAvatar = ({ author }: { author: User | null }) => {
	if (!author) {
		return null;
	}
	if (author.isSuperAdmin) {
		return <SupemboutsAvatarIcon />;
	}
	return <DefaultAvatarIcon />;
};

const ChatMessageContent = ({ message }: { message: TicketMessage }) => {
	if (message.meta) {
		const meta = message.meta as
			| { type: string }
			| { type: "upload"; files: number[] };

		if (meta.type === "upload") {
			return (
				<>
					{(meta as { files: number[] }).files.map((file) => (
						<PreviewFile key={file} id={file} />
					))}
				</>
			);
		}
	}
	return <>{message.content}</>;
};

const ChatMessage = ({ message }: { message: TicketMessage }) => {
	const user = useConnectedUser();
	const { data: author } = useUser(message.authorId);

	if (!user || !author) {
		return null;
	}
	const isAuthor = user.id === author.id;
	return (
		<Grid
			templateColumns={"3rem calc(100% - 6rem) 3rem"}
			gap="4px"
			alignItems={"end"}
		>
			<Box ml={4}>{isAuthor === false && <ChatAvatar author={author} />}</Box>
			<Flex w="100%" justifyContent={isAuthor ? "end" : "start"}>
				<Box w="fit-content" minW={"20rem"}>
					<Flex>
						<Box
							bgColor={isAuthor ? "brand.100" : "gray.100"}
							w="100%"
							p={4}
							borderRadius={"xl"}
							mt={1}
						>
							<Box whiteSpace={"pre-line"}>
								<ChatMessageContent message={message} />
							</Box>
							<Flex
								justifyContent={"end"}
								lineHeight="18px"
								fontSize="12px"
								fontWeight="400"
								color="gray.600"
								mt={2}
							>
								{formatDistanceToNow(message.createdAt)}
							</Flex>
						</Box>
					</Flex>
				</Box>
			</Flex>
			<Box>{isAuthor && <ChatAvatar author={author} />}</Box>
		</Grid>
	);
};

const ChatAuthorName = ({ id }: { id: number }) => {
	const user = useConnectedUser();
	const { data: author } = useUser(id);

	if (!user || !author) {
		return null;
	}
	const isAuthor = user.id === author.id;
	const fullName = `${author.firstName} ${author.lastName}`.trim();
	const displayName = isAuthor
		? "Vous"
		: author.isSuperAdmin
			? fullName
				? `Supembouts (${fullName})`
				: "Supembouts"
			: fullName;

	return (
		<Grid templateColumns={"3rem calc(100% - 6rem) 3rem"}>
			<Box></Box>
			<Flex w="100%" justifyContent={isAuthor ? "end" : "start"}>
				<Text fontSize="14px" fontWeight="600" mt={3}>
					{displayName}
				</Text>
			</Flex>
			<Box></Box>
		</Grid>
	);
};

const ChatMessages = ({ messages }: { messages: TicketMessage[] }) => {
	let prevAuthorId: number | null = null;
	return messages
		.sort(
			(a, b) =>
				new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
		)
		.map((message) => {
			const showName =
				prevAuthorId === null || message.authorId !== prevAuthorId;
			if (showName) {
				prevAuthorId = message.authorId;
			}
			return (
				<>
					{showName && <ChatAuthorName id={message.authorId}></ChatAuthorName>}
					<ChatMessage
						key={message.createdAt.toString()}
						message={message}
					></ChatMessage>
				</>
			);
		});
};

const Chat = ({
	defaultRoom,
	ticketId,
	...props
}: FlexProps & { defaultRoom: string; ticketId: number | null }) => {
	const navigate = useNavigate();
	const [isConnected, setIsConnected] = useState(socket.connected);
	const [messages, setMessages] = useState<TicketMessage[]>([]);
	const [input, setInput] = useState<string>("");
	const toast = useSimpleToast();
	const user = useConnectedUser();
	const messagesQuery = useTicketMessages(ticketId ?? undefined);
	const meta: TicketMeta = useMemo(
		() => ({
			room: {
				name: defaultRoom,
			},
			author: {
				id: user ? user.id : null,
			},
			ticket: {
				id: ticketId,
			},
		}),
		[user, defaultRoom, ticketId],
	);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const ticket = useTicket(ticketId ?? undefined);

	useEffect(() => {
		if (messagesQuery.isError) {
			const error = messagesQuery.error as any;
			if (error?.response?.status === 403) {
				toast.error("Accès refusé", "Vous n'avez pas accès à ce ticket");
				navigate("/tickets");
			}
		}
	}, [messagesQuery.isError, messagesQuery.error, navigate, toast]);

	useEffect(() => {
		if (messagesQuery.data) {
			const formattedMessages = messagesQuery.data.map((msg) => ({
				content: msg.content,
				meta: msg.meta,
				createdAt: new Date(msg.createdAt),
				updatedAt: new Date(msg.updatedAt),
				authorId: msg.authorId,
			}));
			setMessages(formattedMessages);
		}
	}, [messagesQuery.data]);

	useEffect(() => {
		const onConnect = () => {
			if (isConnected === false) {
				setIsConnected(true);
				socket.emit("join", meta);
			}
		};

		const onDisconnect = () => {
			setIsConnected(false);
			toast.error("Déconnexion", "Vous venez d'être déconnecté du chat");
		};

		const onMessage = (meta: TicketMeta, message: string) => {
			setMessages([
				...messages,
				{
					content: message,
					meta: null,
					createdAt: new Date(),
					updatedAt: new Date(),
					authorId: meta.author.id ?? 0,
				},
			]);
		};

		const onHistory = (_meta: TicketMeta, messages: TicketMessage[]) => {
			setMessages(messages);
		};

		socket.on("connect", onConnect);
		socket.on("disconnect", onDisconnect);
		socket.on("message", onMessage);
		socket.on("history", onHistory);

		return () => {
			socket.off("connect", onConnect);
			socket.off("disconnect", onDisconnect);
			socket.off("message", onMessage);
			socket.off("history", onHistory);
		};
	});

	useEffect(() => {
		if (isConnected === false) {
			socket.emit("join", meta);
		}
	});

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (
			event.code === "Enter" &&
			event.shiftKey === false &&
			input.trim() !== ""
		) {
			try {
				socket.emit("message", meta, input);
				setInput("");
				event.preventDefault();
			} catch (err) {
				console.error(err);
			}
		}
	};

	const handleSend = () => {
		socket.emit("message", meta, input);
		setInput("");
	};

	const queryClient = useQueryClient();
	const { mutate: mutateLinkFilesToTicket } = useMutation(linkFilesToTicket, {
		onSuccess: () => {
			queryClient.invalidateQueries("tickets");
			queryClient.invalidateQueries(["tickets", "logs"]);
			queryClient.invalidateQueries(["tickets", "documents"]);
			socket.emit("history", meta);
		},
	});
	const handleAttachFile = (files: FileList) => {
		if (ticketId) {
			mutateLinkFilesToTicket({
				id: ticketId,
				fileList: files,
			});
		}
	};
	const proofModal = useDisclosure();
	const statusRef = useRef<TicketStatus | null | undefined>(ticket?.status);
	const discountRef = useRef<number | null | undefined>(ticket?.discount);
	const { mutate: mutateUpdateTicketStatus } = useMutation(updateTicket, {
		onSuccess: () => {
			queryClient.invalidateQueries("tickets");
			queryClient.invalidateQueries(["tickets", "logs"]);
			queryClient.invalidateQueries(["tickets", "documents"]);
		},
	});
	return (
		<>
			<Grid
				w={props.w ?? "100%"}
				maxW={props.maxW}
				h={"100%"}
				gridTemplateRows={"100px 1fr"}
				position={props.position}
				top={props.top}
				right={props.right}
				flexDirection={"column"}
			>
				<Flex
					borderLeft="solid 1px"
					borderBottom="solid 1px"
					borderColor="gray.200"
					justifyContent="space-between"
					alignItems="center"
					paddingStart={6}
				>
					<Text fontSize="18px" fontWeight={600}>
						Messagerie
					</Text>
					{userIsAdmin(user) && ticket?.status === TicketStatus.OPEN && (
						<Button
							bgColor="brand.600"
							color="white"
							mr={8}
							onClick={() => {
								mutateUpdateTicketStatus({
									id: ticketId ?? undefined,
									status: TicketStatus.CLOSED,
								});
							}}
						>
							Fermer le ticket
						</Button>
					)}
					{userIsAdmin(user) && ticket?.status === TicketStatus.CLOSED && (
						<Button
							bgColor="brand.600"
							color="white"
							mr={8}
							onClick={() => {
								mutateUpdateTicketStatus({
									id: ticketId ?? undefined,
									status: TicketStatus.OPEN,
								});
							}}
						>
							Ouvrir le ticket
						</Button>
					)}
				</Flex>

				<Box h={"100%"} w={props.w ?? "100%"} maxW={props.maxW}>
					{!isConnected && (
						<Flex
							w="100%"
							h="100%"
							justifyContent="center"
							alignItems="center"
							direction={"column"}
						>
							<Spinner
								thickness="4px"
								speed="0.65s"
								emptyColor="gray.200"
								color="brand.700"
								size="xl"
							></Spinner>
							<Text mt={4} lineHeight="21px" fontSize="18px" fontWeight="600">
								Connexion au chat
							</Text>
						</Flex>
					)}
					{isConnected && (
						<Box
							h={"100%"}
							w="100%"
							maxW={props.maxW}
							borderLeft="1px solid #E2E8F0"
						>
							<Box
								h={"100%"}
								maxH={"89vh"}
								w={"100%"}
								maxW={props.maxW}
								overflow={"auto"}
								bgColor={"#FAFCFF"}
							>
								{messages && <ChatMessages messages={messages} />}
								<Box h="200px"></Box>
							</Box>
							<Flex
								position={"absolute"}
								bottom={10}
								right={"20px"}
								zIndex={1000}
								w="calc(100% - 40px)"
								p={1}
								alignItems={"end"}
								backgroundColor={"white"}
								borderRadius={"xl"}
								boxShadow="md"
							>
								<Textarea
									placeholder="Ecrivez un message"
									value={input}
									onChange={(e) => setInput(e.target.value)}
									onKeyDown={handleKeyDown}
									border={"none"}
									h="142px"
								></Textarea>
								<Flex pb={2}>
									<AttachedFileIcon
										mx={4}
										onClick={() => onOpen()}
										color="gray.400"
										w="20px"
										h="20px"
									></AttachedFileIcon>
									<SendIcon
										mx={4}
										onClick={handleSend}
										color="gray.400"
										w="20px"
										h="20px"
									></SendIcon>
								</Flex>
							</Flex>
						</Box>
					)}
				</Box>

				<SimpleModal
					isOpen={proofModal.isOpen}
					onClose={proofModal.onClose}
					acceptText="Confirmer"
					title="Confirmation de preuve"
					onClick={async () => {
						proofModal.onClose();
						if (
							ticketId &&
							statusRef.current != null &&
							discountRef.current != null
						) {
							mutateUpdateTicketStatus({
								id: ticketId,
								status: statusRef.current,
								discount: discountRef.current,
							});
						}
					}}
				>
					<Text>Ce ticket a besoin d'une preuve pour être fermé.</Text>
					<Text>Pouvez-vous confirmer qu'une preuve a été donnée ?</Text>
				</SimpleModal>
			</Grid>

			<AttachedFileModal
				isOpen={isOpen}
				onClose={onClose}
				onClick={handleAttachFile}
			></AttachedFileModal>
		</>
	);
};

export default Chat;
